import { createContext, useContext, useState, useEffect, ReactNode, Context } from "react";
import { en } from "@/i18n/en";
import { es } from "@/i18n/es";

export type Language = "en" | "es";
export type Translations = typeof en;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: Translations;
}

// Cache context on globalThis so React Fast Refresh doesn't create a new
// context instance (which would break the Provider/Consumer pairing).
const globalKey = "__ibylit_language_context__";
const LanguageContext: Context<LanguageContextType | undefined> =
  (globalThis as any)[globalKey] ??
  ((globalThis as any)[globalKey] = createContext<LanguageContextType | undefined>(undefined));
LanguageContext.displayName = "LanguageContext";

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const DEFAULTS_VERSION = "v2";
      if (localStorage.getItem("ibylit-defaults-version") !== DEFAULTS_VERSION) {
        localStorage.removeItem("theme");
        localStorage.removeItem("ibylit-lang");
        localStorage.setItem("ibylit-defaults-version", DEFAULTS_VERSION);
      }
    } catch {}
    const stored = localStorage.getItem("ibylit-lang");
    if (stored === "en") return "en";
    return "es";
  });

  useEffect(() => {
    localStorage.setItem("ibylit-lang", language);
  }, [language]);

  const toggleLanguage = () => setLanguage((prev) => (prev === "en" ? "es" : "en"));

  const t = language === "en" ? en : es;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within a LanguageProvider");
  return context;
};
